import React, { useState } from "react";
import {
  Download,
  Trash2,
  Eye,
  Search,
  Filter,
  File,
  FileText,
  Image,
  Video,
  Music,
  Archive,
  FileSpreadsheet,
  FileImage,
  Calendar,
  User,
  Tag,
  MoreVertical,
} from "lucide-react";
import { useFiles } from "../../context/FileContext";
import { FileRecord } from "../../context/FileContext";
import Button from "./Button";
import Input from "./Input";
import Badge from "./Badge";
import { format } from "date-fns";

interface FileBrowserProps {
  category?: string;
  onFileSelect?: (file: FileRecord) => void;
  showUpload?: boolean;
  className?: string;
}

export default function FileBrowser({
  category,
  onFileSelect,
  showUpload = true,
  className = "",
}: FileBrowserProps) {
  const { state, downloadFile, deleteFile, getFilesByCategory, searchFiles } =
    useFiles();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(category || "all");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(
    null
  );

  const categories = [
    { id: "all", name: "All Files", icon: File },
    { id: "governance", name: "Governance", icon: FileText },
    { id: "membership", name: "Membership", icon: User },
    { id: "finance", name: "Finance", icon: FileSpreadsheet },
    { id: "legal", name: "Legal", icon: FileText },
    { id: "media", name: "Media", icon: Image },
    { id: "admin", name: "Administration", icon: File },
  ];

  // Get filtered files
  const getFilteredFiles = (): FileRecord[] => {
    let files =
      selectedCategory === "all"
        ? state.files
        : getFilesByCategory(selectedCategory);

    if (searchTerm) {
      files = searchFiles(searchTerm);
    }

    return files.sort(
      (a, b) =>
        new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()
    );
  };

  const getFileIcon = (file: FileRecord) => {
    const type = file.type.toLowerCase();
    if (type.includes("pdf")) return FileText;
    if (type.includes("image")) return Image;
    if (type.includes("video")) return Video;
    if (type.includes("audio")) return Music;
    if (type.includes("zip") || type.includes("rar")) return Archive;
    if (type.includes("excel") || type.includes("spreadsheet"))
      return FileSpreadsheet;
    if (type.includes("word") || type.includes("document")) return FileText;
    return File;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleDelete = (fileId: string) => {
    deleteFile(fileId);
    setShowDeleteConfirm(null);
  };

  const filteredFiles = getFilteredFiles();

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search files..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        <div className="flex gap-2">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* File Grid */}
      {filteredFiles.length === 0 ? (
        <div className="text-center py-12">
          <File className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No files found
          </h3>
          <p className="text-gray-500">
            {searchTerm
              ? "Try adjusting your search terms"
              : "Upload your first file to get started"}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredFiles.map((file) => {
            const FileIcon = getFileIcon(file);
            return (
              <div
                key={file.id}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-gray-100 rounded-lg">
                      <FileIcon className="h-6 w-6 text-gray-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-gray-900 truncate">
                        {file.name}
                      </h4>
                      <p className="text-xs text-gray-500">
                        {formatFileSize(file.size)}
                      </p>
                    </div>
                  </div>
                  <div className="relative">
                    <button className="text-gray-400 hover:text-gray-600">
                      <MoreVertical className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {file.description && (
                  <p className="text-xs text-gray-600 mb-3 line-clamp-2">
                    {file.description}
                  </p>
                )}

                <div className="flex flex-wrap gap-1 mb-3">
                  <Badge variant="info" size="sm">
                    {file.category}
                  </Badge>
                  {file.isPublic && (
                    <Badge variant="success" size="sm">
                      Public
                    </Badge>
                  )}
                </div>

                <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                  <div className="flex items-center space-x-1">
                    <Calendar className="h-3 w-3" />
                    <span>
                      {format(new Date(file.uploadedAt), "MMM dd, yyyy")}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Download className="h-3 w-3" />
                    <span>{file.downloadCount}</span>
                  </div>
                </div>

                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => downloadFile(file.id)}
                    className="flex-1"
                  >
                    <Download className="h-3 w-3 mr-1" />
                    Download
                  </Button>
                  {onFileSelect && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onFileSelect(file)}
                    >
                      <Eye className="h-3 w-3" />
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowDeleteConfirm(file.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Delete File
            </h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete this file? This action cannot be
              undone.
            </p>
            <div className="flex space-x-3">
              <Button
                variant="secondary"
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleDelete(showDeleteConfirm)}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
